/**
 * vibecheck commit-shield — Commit risk analysis (ShipGate-powered)
 *
 * Free: warnings only. Pro+: blocking, risk scoring, evidence bundles, auditor mode.
 * Subcommands: run (default), install, uninstall, audit, report, policy init.
 */

import * as path from 'path';
import type { Command } from 'commander';
import type { GlobalOptions } from '../types';
import { colors, icons } from '../ui/theme';

type CommitShieldTier = 'free' | 'pro' | 'team' | 'enterprise';

export function registerCommitShield(program: Command): void {
  const cmd = program
    .command('commit-shield')
    .description('Analyze staged changes for risk (CommitShield). Free: warnings only; Pro+: blocking & evidence.')
    .option('-p, --path <dir>', 'Project directory', process.cwd())
    .option('--tier <tier>', 'Tier: free, pro, team, enterprise', 'free')
    .option('--json', 'Output machine-readable JSON report')
    .option('--ci', 'CI mode: exit 1 on BLOCK, minimal output');

  cmd
    .command('run')
    .description('Analyze staged files (default subcommand)')
    .option('--threshold <n>', 'Block if risk score above (0-100)', (v) => parseInt(v, 10), 75)
    .option('--policy <path>', 'Path to commit-shield policy YAML')
    .action(async (subOpts: Record<string, unknown>, command: Command) => {
      const globals = command.parent!.parent!.opts() as GlobalOptions;
      const parentOpts = command.parent!.opts() as Record<string, unknown>;
      const projectRoot = path.resolve((parentOpts.path as string) ?? globals.path ?? process.cwd());
      const { runCommitShieldCLI } = await import('@vibecheck/core/commit-shield');
      const tier = (parentOpts.tier as CommitShieldTier) ?? 'free';
      const { exitCode } = await runCommitShieldCLI({
        projectRoot,
        tier,
        ci: !!globals.ci || !!parentOpts.ci || !!subOpts.ci,
        json: !!globals.json || !!parentOpts.json || !!subOpts.json,
        threshold: subOpts.threshold as number | undefined,
        policyPath: subOpts.policy as string | undefined,
      });
      process.exit(exitCode);
    });

  cmd
    .command('install')
    .description('Install git pre-commit hook to run CommitShield')
    .action(async (_subOpts: Record<string, unknown>, command: Command) => {
      const parentOpts = command.parent!.opts() as Record<string, unknown>;
      const projectRoot = path.resolve((parentOpts.path as string) ?? process.cwd());
      const { runCommitShieldCLI } = await import('@vibecheck/core/commit-shield');
      const { exitCode } = await runCommitShieldCLI({ projectRoot, install: true });
      process.exit(exitCode);
    });

  cmd
    .command('uninstall')
    .description('Remove CommitShield pre-commit hook')
    .action(async (_subOpts: Record<string, unknown>, command: Command) => {
      const parentOpts = command.parent!.opts() as Record<string, unknown>;
      const projectRoot = path.resolve((parentOpts.path as string) ?? process.cwd());
      const { runCommitShieldCLI } = await import('@vibecheck/core/commit-shield');
      const { exitCode } = await runCommitShieldCLI({ projectRoot, uninstall: true });
      process.exit(exitCode);
    });

  cmd
    .command('audit')
    .description('Run with auditor explanation (Pro+): SOC2/ISO/HIPAA framing')
    .option('--threshold <n>', 'Block if risk score above (0-100)', (v) => parseInt(v, 10), 75)
    .action(async (subOpts: Record<string, unknown>, command: Command) => {
      const globals = command.parent!.parent!.opts() as GlobalOptions;
      const parentOpts = command.parent!.opts() as Record<string, unknown>;
      const projectRoot = path.resolve((parentOpts.path as string) ?? globals.path ?? process.cwd());
      const { runCommitShieldCLI } = await import('@vibecheck/core/commit-shield');
      const tier = (parentOpts.tier as CommitShieldTier) ?? 'pro';
      const { exitCode } = await runCommitShieldCLI({
        projectRoot,
        tier,
        audit: true,
        ci: !!globals.ci,
        json: !!globals.json || !!parentOpts.json,
        threshold: subOpts.threshold as number | undefined,
      });
      process.exit(exitCode);
    });

  cmd
    .command('report')
    .description('Generate machine-readable risk report (Pro+)')
    .option('-o, --output <file>', 'Write report to file (default: stdout)')
    .action(async (subOpts: Record<string, unknown>, command: Command) => {
      const globals = command.parent!.parent!.opts() as GlobalOptions;
      const parentOpts = command.parent!.opts() as Record<string, unknown>;
      const projectRoot = path.resolve((parentOpts.path as string) ?? globals.path ?? process.cwd());
      const { runCommitShieldCLI, CommitShieldEngine } = await import('@vibecheck/core/commit-shield');
      const tier = (parentOpts.tier as CommitShieldTier) ?? 'pro';
      const outputFile = subOpts.output as string | undefined;
      const { exitCode, result } = await runCommitShieldCLI({
        projectRoot,
        tier,
        json: !outputFile,
      });
      if (result && outputFile) {
        const fs = await import('fs/promises');
        const engine = new CommitShieldEngine({ projectRoot, tier });
        const report = engine.toReport(result);
        await fs.writeFile(outputFile, JSON.stringify(report, null, 2), 'utf-8');
        if (!globals.quiet) {
          console.log(colors.dim(`Report written to ${outputFile}`));
        }
      }
      process.exit(exitCode);
    });

  const policyCmd = cmd.command('policy').description('Policy configuration');
  policyCmd
    .command('init')
    .description('Create .vibecheck/commit-shield.yaml template')
    .option('-p, --path <dir>', 'Project directory for .vibecheck/commit-shield.yaml', process.cwd())
    .action(async (subOpts: Record<string, unknown>, command: Command) => {
      const commitShieldCmd = command.parent!.parent!;
      const parentOpts = commitShieldCmd.opts() as Record<string, unknown>;
      let root: Command | undefined = command;
      while (root?.parent) root = root.parent;
      const globals = root?.opts() as GlobalOptions | undefined;
      const projectRoot = path.resolve(
        (subOpts.path as string) ??
          (parentOpts.path as string) ??
          globals?.path ??
          process.cwd(),
      );
      const { runCommitShieldCLI } = await import('@vibecheck/core/commit-shield');
      const { exitCode } = await runCommitShieldCLI({ projectRoot, policyInit: true });
      process.exit(exitCode);
    });

  // Default: run
  cmd.action(async (_opts: Record<string, unknown>, command: Command) => {
    const globals = command.parent!.opts() as GlobalOptions;
    const parentOpts = command.opts() as Record<string, unknown>;
    const projectRoot = path.resolve((parentOpts.path as string) ?? globals.path ?? process.cwd());
    const { runCommitShieldCLI } = await import('@vibecheck/core/commit-shield');
    const tier = (parentOpts.tier as CommitShieldTier) ?? 'free';
    const { exitCode } = await runCommitShieldCLI({
      projectRoot,
      tier,
      ci: !!globals.ci,
      json: !!globals.json,
    });
    process.exit(exitCode);
  });
}
